if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
          navPrev: this.querySelector('.product-gallery-nav__button--prev'),
          navNext: this.querySelector('.product-gallery-nav__button--next'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        
        // Setup navigation arrows
        if (this.elements.navPrev && this.elements.navNext) {
          this.elements.navPrev.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            this.navigatePrev();
          });
          this.elements.navNext.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            this.navigateNext();
          });
          
          // Add keyboard navigation
          document.addEventListener('keydown', (e) => {
            if (e.key === 'ArrowLeft') {
              e.preventDefault();
              this.navigatePrev();
            } else if (e.key === 'ArrowRight') {
              e.preventDefault();
              this.navigateNext();
            }
          });
          
          // Initialize button states
          setTimeout(() => this.updateNavigationButtons(), 100);
        }
        
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
        
        // Update navigation button states
        this.updateNavigationButtons();
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }

      navigatePrev() {
        if (!this.elements.thumbnails) return;
        
        const allThumbnails = Array.from(this.elements.thumbnails.querySelectorAll('[data-target]'));
        const currentActive = this.elements.thumbnails.querySelector('button[aria-current="true"]');
        
        if (!currentActive) return;
        
        const currentThumbnail = currentActive.closest('[data-target]');
        const currentIndex = allThumbnails.indexOf(currentThumbnail);
        
        if (currentIndex > 0) {
          const prevThumbnail = allThumbnails[currentIndex - 1];
          this.setActiveMedia(prevThumbnail.dataset.target, false);
        }
      }

      navigateNext() {
        if (!this.elements.thumbnails) return;
        
        const allThumbnails = Array.from(this.elements.thumbnails.querySelectorAll('[data-target]'));
        const currentActive = this.elements.thumbnails.querySelector('button[aria-current="true"]');
        
        if (!currentActive) return;
        
        const currentThumbnail = currentActive.closest('[data-target]');
        const currentIndex = allThumbnails.indexOf(currentThumbnail);
        
        if (currentIndex < allThumbnails.length - 1) {
          const nextThumbnail = allThumbnails[currentIndex + 1];
          this.setActiveMedia(nextThumbnail.dataset.target, false);
        }
      }

      updateNavigationButtons() {
        if (!this.elements.navPrev || !this.elements.navNext || !this.elements.thumbnails) return;
        
        const allThumbnails = Array.from(this.elements.thumbnails.querySelectorAll('[data-target]'));
        const currentActive = this.elements.thumbnails.querySelector('button[aria-current="true"]');
        
        if (!currentActive) return;
        
        const currentThumbnail = currentActive.closest('[data-target]');
        const currentIndex = allThumbnails.indexOf(currentThumbnail);
        
        // Disable prev button if at first image
        if (currentIndex === 0) {
          this.elements.navPrev.setAttribute('disabled', 'disabled');
        } else {
          this.elements.navPrev.removeAttribute('disabled');
        }
        
        // Disable next button if at last image
        if (currentIndex === allThumbnails.length - 1) {
          this.elements.navNext.setAttribute('disabled', 'disabled');
        } else {
          this.elements.navNext.removeAttribute('disabled');
        }
      }
    }
  );
}
